<template>
    <p-modal name="productSalesMarginModal"
             label="Product Sales &amp; Margin"
             ref="productSalesMarginModal"
             size="lg"
             @ok.prevent="handleModalOk">
        <template v-slot:default>
            <p-form ref="productSalesMarginForm">
                <div class="container-fluid content-wrapper">
                    <b-row>
                        <b-col>
                            <p>Product sales with cost of goods and margin</p>
                        </b-col>
                    </b-row>
                    <b-row cols="1" cols-lg="2">
                        <b-col>
                            <p-card title="Date Range">
                                <p-date-range-widget :date-range-options="reportParameters" />
                            </p-card>
                        </b-col>
                        <b-col>
                            <p-card title="Additional Options">
                                <b-row>
                                    <b-col>
                                        <p-checkbox size="md"
                                                    label="Include DCC Subtotals"
                                                    v-model="reportParameters.includeDccSubtotals" />
                                        <p-checkbox size="md"
                                                    label="Provide CSV Export"
                                                    v-model="reportParameters.provideCsvExport" />
                                    </b-col>
                                </b-row>
                            </p-card>
                        </b-col>
                    </b-row>
                    <UserReportTemplate
                        :user-report-template-options="userReportTemplateOptions"
                        v-model:selected-user-report-template-id="selectedUserReportTemplateId"
                        :selected-user-report-template="selectedUserReportTemplate"
                        v-model:template-name="templateName"
                        @selected-object="applyUserReportTemplate($event)" />
                </div>
            </p-form>
        </template>
        <template v-slot:modal-footer="{ ok, cancel }">
            <b-row>
                <b-col>
                    <p-button variant="outline-primary"
                              :is-busy="isBusy"
                              @click="cancel">Cancel</p-button>
                    <p-button variant="primary"
                              :is-busy="isBusy"
                              class="ms-2"
                              @click="ok">OK</p-button>
                </b-col>
            </b-row>
        </template>
    </p-modal>
</template>

<script>
import axios from 'axios';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import ReportTemplates from '@/components/mixins/ReportTemplates.js';
import UserReportTemplate from '@/components/UserReportTemplate.vue';

export default {
    mixins: [ReportTemplates],
    components: { UserReportTemplate },
    data() {
        return {
            isBusy: false,
            // Identifies which report saved templates belong to; must match the report name in Reports.vue.
            reportName: 'Product Sales & Margin',
            reportParameters: {
                startDate: null,
                endDate: null,
                dateOption: 0,
                includeDccSubtotals: true,
                provideCsvExport: false,
                locationId: null
            }
        };
    },
    methods: {
        resetForm() {
            this.reportParameters.dateOption = 0;
            this.reportParameters.startDate = new Date().toISOString();
            this.reportParameters.endDate = new Date().toISOString();
            this.reportParameters.includeDccSubtotals = true;
            this.reportParameters.provideCsvExport = false;
        },
        async show() {
            this.resetForm();
            this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
            await this.loadUserReportTemplates();
            this.$refs.productSalesMarginModal.show();
        },
        async handleModalOk() {
            if (!(await this.$refs.productSalesMarginForm.validate())) {
                return;
            }

            this.isBusy = true;

            // The current location always wins: a restored template carries the location that was
            // active when it was saved, which would otherwise run the report for the wrong one.
            this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();

            if (!(await this.saveUserReportTemplate())) {
                this.isBusy = false;
                return;
            }

            try {
                const pdf = await axios.post('sales/exportProductSalesMargin', this.reportParameters, {
                    responseType: 'blob'
                });

                downloadFileToBrowser(pdf.data, `${this.reportName}.pdf`, 'application/pdf');

                if (this.reportParameters.provideCsvExport) {
                    const csv = await axios.post('sales/exportProductSalesMarginCsv', this.reportParameters, {
                        responseType: 'blob'
                    });

                    downloadFileToBrowser(csv.data, `${this.reportName}.csv`, 'application/csv');
                }

                this.$appSuccess(`${this.reportName} report generated successfully.`);
                this.$refs.productSalesMarginModal.hide();
            } finally {
                this.isBusy = false;
            }
        }
    }
};
</script>
